import { ActivitySpinnerSize, ActivitySpinnerWidget, ButtonType, ButtonWidget, TabStyles, TextAreaWidget } from '@tableau/widgets';
import * as React from 'react';
import { connect } from 'react-redux';
import { style } from 'typestyle';

import { Messages } from '../../../codegen/Localize';
import { IMetricInfo } from '../../models/IMetricInfo';
import { Metrics } from '../../resources/Metrics';
import { ISetDescriptionAction } from '../../../ts/ServerApi/types/actionNames';
import { selectAvailableActionsForEntity } from '../../../app/selectors/EntityActionsSelectors';
import { MapStateToProps } from '../../../app/utils/ConnectUtils';
import { MarkupField } from '../../../app/spaces/Components/MarkupField';
import { editBlockStyleClass, editButtonClass, metadataSectionStyle, metadataSubTitle, metadataText, resolveButton, resolveButtonContainer } from './MetricsStyles';
import { characterCounter } from '../../../app/content/CharacterCounter';

export interface MetricDescriptionSectionOwnProps {
  metricInfo: IMetricInfo;
  descriptionChanged?: () => void;
  setMetricDescription?: typeof Metrics.setMetricDescription;
}

export interface MetricDescriptionSectionStateProps {
  canEditDescription: boolean;
}

export type MetricDescriptionSectionProps = MetricDescriptionSectionOwnProps & MetricDescriptionSectionStateProps;

export interface MetricDescriptionSectionState {
  isEditingDescription: boolean;
  displayedDescription: string;
  savedDescription: string;
  isSaving: boolean;
}

const descriptionMaxLength: number = 255;

export class MetricDescriptionSection extends React.PureComponent<MetricDescriptionSectionProps, MetricDescriptionSectionState> {
  public constructor(props: Readonly<MetricDescriptionSectionProps>) {
    super(props);
    this.state = {
      isEditingDescription: false,
      displayedDescription: props.metricInfo.description,
      savedDescription: props.metricInfo.description,
      isSaving: false
    };
  }

  private handleEditClick = (event: React.MouseEvent) => {
    const isTargetElementAnAnchorTag = (event.target as HTMLElement).tagName.toLowerCase() === 'a';

    // If an anchor tag is clicked do not enter editing mode
    if (isTargetElementAnAnchorTag) return;

    this.setState({ isEditingDescription: true });
  }

  public render(): JSX.Element {
    const canEnterEditModeWithClick = this.props.canEditDescription && !this.state.isEditingDescription;
    const editModeHandler = canEnterEditModeWithClick ? this.handleEditClick : undefined;

    return (
      <div
      className={[metadataSectionStyle, canEnterEditModeWithClick ? editBlockStyleClass : ''].join(' ')}
        onClick={editModeHandler}
        data-tb-test-id={'metadata-description-container'}
      >
        <div style={descriptionTitleStyle}>
          <div style={metadataSubTitle} id="description-label">{Messages.metricsMetadataPanelDescription()}</div>
          {canEnterEditModeWithClick ?
            <button className={editButtonClass} aria-label={Messages.metricsMetadataPanelEditDescriptionLabel()} onClick={editModeHandler} />
            : null
          }
        </div>
        {this.renderDescription()}
      </div>
    );
  }

  private renderDescription = () => {
    if (this.state.isEditingDescription) {

      const isTextAreaValid = !(this.state.displayedDescription.length > descriptionMaxLength);

      return (
        <div style={metadataText} aria-labeled-by="description-label">
          <TextAreaWidget
            label=""
            testId="description-editor"
            value={this.state.displayedDescription}
            onChange={e => this.descriptionChangeCallback(e.target.value)}
            message={characterCounter({ value: this.state.displayedDescription, maximum: descriptionMaxLength })}
            valid={isTextAreaValid ? undefined : false}
            maxLength={descriptionMaxLength}
            rows={3}
            style={descriptionTextArea}
            className={style({ width: '100%' })}
            disabled={this.state.isSaving}
          />
          <div style={{...resolveButtonContainer, marginTop: '3px'}}>
            <ButtonWidget
              style={resolveButton}
              buttonType={ButtonType.Outline}
              onClick={this.handleCancelClick}
              testId="cancel-description-edit"
              disabled={this.state.isSaving}
            >
              {Messages.cancel()}
            </ButtonWidget>
            <ButtonWidget
              style={resolveButton}
              buttonType={ButtonType.Primary}
              onClick={this.handleSaveClick}
              testId="save-description"
            >
            {
              this.state.isSaving ?
                <div style={{ marginTop: TabStyles.Spacing.SmallSpace! }}>
                  <ActivitySpinnerWidget onDarkBackground spinnerSize={ActivitySpinnerSize.Small} testId="submitting" />
                </div> :
                <span>{Messages.save()}</span>
            }
            </ButtonWidget>
          </div>
        </div>
      );
    } else {
      const descriptionToDisplay = this.state.displayedDescription || Messages.metricsMetadataPanelNoDescription();
      return (
        <div style={metadataText} aria-labelledby="description-label">
          <MarkupField testId={'metadata-description'} markup={descriptionToDisplay} />
        </div>
      );
    }
  }

  private descriptionChangeCallback = (userInput: string) => {
    this.setState({ displayedDescription: userInput });
  }

  private handleSaveClick = () => {
    if (this.state.isSaving) return;

    this.setState({
      isSaving: true,
    }, this.saveDescriptionAndStopEditing);
  }

  private saveDescriptionAndStopEditing = async () => {
    try {
      const setDescriptionFunc = this.props.setMetricDescription ? this.props.setMetricDescription : Metrics.setMetricDescription;
      await setDescriptionFunc({
        description: this.state.displayedDescription,
        id: this.props.metricInfo.id,
      });
      this.setState({
        isEditingDescription: false,
        savedDescription: this.state.displayedDescription,
        isSaving: false
      });

      if (this.props.descriptionChanged) {
        this.props.descriptionChanged();
      }
    }
    catch {
      this.setState({
        isEditingDescription: true,
        isSaving: false
      });
    }
  }

  private handleCancelClick = () => {
    this.setState({
      isEditingDescription: false,
      displayedDescription: this.state.savedDescription,
    });
  }
}

const mapStateToProps: MapStateToProps<MetricDescriptionSectionStateProps, MetricDescriptionSectionOwnProps> = (state, ownProps) => {
  const availableActions = selectAvailableActionsForEntity(state, ownProps.metricInfo);
  return {
    canEditDescription: availableActions && (availableActions as ISetDescriptionAction).setDescription
  };
};

export const ConnectedMetricDescriptionSection: React.ComponentClass<MetricDescriptionSectionOwnProps>
  = connect(mapStateToProps, undefined)(MetricDescriptionSection);

const descriptionTextArea: React.CSSProperties = {
  width: '100%',
  resize: 'none',
  display: 'block',
};

const descriptionTitleStyle = {
  display: 'flex',
};
